<!--
  MessageBubble — Phase 4-3 / T14.

  Renders a single message. The parent Thread component handles burst grouping
  and passes `isFirstOfBurst` / `isLastOfBurst` so the bubble can decide which
  ornaments to show (sender label, timestamp, status pill, anchor corner).

  Phase 4 (outbound reactions): customer (inbound) bubbles get a hover-revealed
  reaction trigger. Clicking opens a 6-emoji popover that POSTs to the
  reactions endpoint. The operator pill renders at the opposite corner from
  the customer pill so both coexist.
-->
<script lang="ts">
  import { createMutation, createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { reactToMessageMutation } from '../lib/api/mutations.js';
  import { attachmentUrl, type Message } from '../lib/api/queries.js';

  type Status = 'PENDING' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';

  type Props = {
    message: Message;
    isFirstOfBurst: boolean;
    isLastOfBurst: boolean;
    senderName?: string;
    status?: Status;
    onRetry?: (id: string) => void;
  };

  let {
    message,
    isFirstOfBurst,
    isLastOfBurst,
    senderName,
    status,
    onRetry,
  }: Props = $props();

  // Attachment bucket is private: `message.attachmentUrl` identifies the
  // object but isn't loadable. Fetch a signed URL, and only for messages that
  // actually have one so text bubbles cost nothing.
  const hasAttachment = $derived(
    (message.type === 'image' || message.type === 'video') && Boolean(message.attachmentUrl),
  );
  const attachmentQuery = $derived(
    createQuery({
      ...attachmentUrl(message.conversationId, message.id),
      enabled: hasAttachment,
    }),
  );
  const attachmentSrc = $derived($attachmentQuery.data?.url);
  const attachmentFailed = $derived($attachmentQuery.isError);

  const isOutbound = $derived(message.direction === 'outbound');
  const isInbound = $derived(!isOutbound);
  const showSender = $derived(isFirstOfBurst && Boolean(senderName));
  const showFooter = $derived(isLastOfBurst);

  const timeLabel = $derived(formatClock(message.createdAt));

  // --- Reactions (Phase 3 inbound + Phase 4 outbound) --------------------
  const PICKER_EMOJIS = ['\u2764\uFE0F', '\u{1F606}', '\u{1F62F}', '\u{1F622}', '\u{1F620}', '\u{1F44D}'];

  // Meta delivers customer reactions as raw Unicode (e.g. U+2764 for ❤).
  // U+2764 alone renders as a text-mode heart "♥" in most browsers — we
  // need the Variation Selector-16 (U+FE0F) suffix to force emoji
  // presentation. The other 5 emojis in Meta's reaction set are surrogate
  // pairs in the U+1F6xx range which always render as emoji, so this
  // touches only the heart.
  function toEmojiPresentation(s: string | undefined): string | undefined {
    if (!s) return s;
    if (s === '\u2764') return '\u2764\uFE0F';
    return s;
  }
  const customerEmoji = $derived(toEmojiPresentation(message.reactions?.customer?.emoji));
  const operatorEmoji = $derived(toEmojiPresentation(message.reactions?.operator?.emoji));
  const showOperatorPill = $derived(isInbound && Boolean(operatorEmoji));
  const showCustomerPill = $derived(Boolean(customerEmoji));
  const dualPills = $derived(isInbound && showCustomerPill && showOperatorPill);
  const hasAnyPill = $derived(showCustomerPill || showOperatorPill);

  const queryClient = useQueryClient();
  const reactMutation = $derived(
    createMutation(reactToMessageMutation(queryClient, message.conversationId)),
  );

  let pickerOpen = $state(false);
  let pickerEl: HTMLDivElement | null = $state(null);
  let triggerEl: HTMLButtonElement | null = $state(null);

  function togglePicker() {
    pickerOpen = !pickerOpen;
  }

  function onPick(emoji: string) {
    const nextEmoji = operatorEmoji === emoji ? null : emoji;
    $reactMutation.mutate({ messageId: message.id, emoji: nextEmoji });
    pickerOpen = false;
  }

  function unreact() {
    $reactMutation.mutate({ messageId: message.id, emoji: null });
  }

  $effect(() => {
    if (!pickerOpen) return;
    function handleMousedown(e: MouseEvent) {
      const target = e.target as Node | null;
      if (!target) return;
      if (pickerEl?.contains(target)) return;
      if (triggerEl?.contains(target)) return;
      pickerOpen = false;
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') pickerOpen = false;
    }
    document.addEventListener('mousedown', handleMousedown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleMousedown);
      document.removeEventListener('keydown', handleKey);
    };
  });

  function formatClock(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }

  function handleRetry() {
    onRetry?.(message.id);
  }
</script>

<div class="row" class:outbound={isOutbound} class:first={isFirstOfBurst} class:last={isLastOfBurst}>
  {#if showSender}
    <div class="sender">{senderName}</div>
  {/if}

  <div class="bubble-wrap" class:has-pill={hasAnyPill}>
    <div class="bubble" class:anchor-end={isOutbound && isLastOfBurst} class:anchor-start={!isOutbound && isLastOfBurst}>
      {#if message.text}
        <span class="body">{message.text}</span>
      {:else if message.type === 'image' && message.attachmentUrl}
        {#if attachmentSrc}
          <img class="attachment" src={attachmentSrc} alt="" />
        {:else if attachmentFailed}
          <span class="body muted">(image unavailable)</span>
        {:else}
          <span class="body muted">Loading image…</span>
        {/if}
      {:else if message.type === 'video' && message.attachmentUrl}
        <!-- Customer-sent videos arrive from Meta CDN with no caption
             track and no transcript source. Synthesizing an empty
             <track> would mislead AT users into expecting captions
             that don't exist; the truthful representation is "no
             captions available." Skip the lint accordingly. -->
        {#if attachmentSrc}
          <!-- svelte-ignore a11y_media_has_caption -->
          <video class="attachment" src={attachmentSrc} controls></video>
        {:else if attachmentFailed}
          <span class="body muted">(video unavailable)</span>
        {:else}
          <span class="body muted">Loading video…</span>
        {/if}
      {:else}
        <span class="body muted">(no content)</span>
      {/if}
    </div>

    {#if hasAnyPill}
      <div class="reactions" class:dual={dualPills}>
        {#if showCustomerPill}
          <span
            class="reaction-pill customer"
            aria-label={`Customer reacted ${customerEmoji}`}
          >
            {customerEmoji}
          </span>
        {/if}
        {#if showOperatorPill}
          <button
            class="reaction-pill operator"
            type="button"
            onclick={unreact}
            aria-label={`Remove your ${operatorEmoji} reaction`}
          >
            {operatorEmoji}
          </button>
        {/if}
      </div>
    {/if}

    {#if isInbound}
      <button
        bind:this={triggerEl}
        class="reaction-trigger"
        class:open={pickerOpen}
        type="button"
        onclick={togglePicker}
        aria-haspopup="menu"
        aria-expanded={pickerOpen}
        aria-label="Add reaction"
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" stroke-width="1.2" />
          <circle cx="5.7" cy="6.7" r="0.9" fill="currentColor" />
          <circle cx="10.3" cy="6.7" r="0.9" fill="currentColor" />
          <path d="M5.5 9.5c0.7 1.1 1.5 1.6 2.5 1.6s1.8-0.5 2.5-1.6" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
        </svg>
      </button>
      {#if pickerOpen}
        <div bind:this={pickerEl} class="picker" role="menu">
          {#each PICKER_EMOJIS as e}
            <button
              class="picker-btn"
              class:selected={operatorEmoji === e}
              type="button"
              onclick={() => onPick(e)}
              role="menuitem"
              aria-label={`React with ${e}`}
            >
              {e}
            </button>
          {/each}
        </div>
      {/if}
    {/if}
  </div>

  {#if showFooter}
    <div class="footer">
      {#if timeLabel}<span class="time">{timeLabel}</span>{/if}
      {#if isOutbound && status}
        {#if timeLabel}<span class="sep" aria-hidden="true">·</span>{/if}
        {#if status === 'PENDING'}
          <span class="status pending">Sending</span>
        {:else if status === 'SENT'}
          <span class="status">Sent</span>
        {:else if status === 'DELIVERED'}
          <span class="status">Delivered</span>
        {:else if status === 'READ'}
          <span class="status read">Read</span>
        {:else if status === 'FAILED'}
          <span class="status failed">Failed to send.</span>
          <button class="retry" type="button" onclick={handleRetry}>Retry</button>
        {/if}
      {/if}
    </div>
  {/if}
</div>

<style>
  .row {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 2px;
    animation: fade-in 160ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .row.outbound {
    align-items: flex-end;
  }
  .row.last {
    margin-bottom: 14px;
  }

  .sender {
    font-size: 11px;
    font-weight: 600;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    margin: 0 0 2px 12px;
  }
  .row.outbound .sender {
    margin: 0 12px 2px 0;
  }

  .bubble-wrap {
    display: flex;
    flex-direction: column;
    align-items: inherit;
    position: relative;
    max-width: 75%;
  }
  .bubble-wrap.has-pill {
    margin-bottom: 6px;
  }

  /* Reactions row sits flush under the bubble. Pills nest into the bubble's
     bottom edge via negative margin-top; horizontal alignment of customer
     vs operator pill matches the message side. */
  .reactions {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-top: -10px;
    padding: 0 8px;
    width: 100%;
    box-sizing: border-box;
    pointer-events: none;
  }
  .reactions > * {
    pointer-events: auto;
  }
  .row.outbound .reactions {
    justify-content: flex-end;
  }
  .row:not(.outbound) .reactions {
    justify-content: flex-start;
  }
  /* When both customer and operator pills are present on an inbound bubble,
     anchor them to opposite corners so both are simultaneously visible. */
  .row:not(.outbound) .reactions.dual {
    justify-content: space-between;
  }

  .reaction-pill {
    padding: 1px 6px;
    border-radius: 999px;
    background: var(--widget-bg, oklch(99% 0.003 250));
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    color: var(--widget-fg, oklch(20% 0.01 250));
    font-size: 12px;
    line-height: 1.2;
    box-shadow: 0 1px 2px color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 8%, transparent);
    animation: fade-in 180ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  button.reaction-pill {
    cursor: pointer;
    font-family: inherit;
  }
  button.reaction-pill:hover {
    background: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 4%, var(--widget-bg, oklch(99% 0.003 250)));
  }
  button.reaction-pill:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 2px;
  }
  .reaction-pill.operator {
    border-color: var(--widget-accent-deep, oklch(50% 0.19 65));
  }

  /* Hover-revealed reaction trigger on inbound bubbles. Sits at the bubble's
     top-right (outside corner) so it doesn't obscure body text. */
  .reaction-trigger {
    position: absolute;
    top: -10px;
    right: -10px;
    width: 24px;
    height: 24px;
    border-radius: 999px;
    background: var(--widget-bg, oklch(99% 0.003 250));
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    opacity: 0;
    transform: scale(0.92);
    transition: opacity 120ms ease-out, transform 120ms ease-out;
    z-index: 2;
    box-shadow: 0 1px 3px color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 10%, transparent);
  }
  .reaction-trigger svg {
    width: 14px;
    height: 14px;
    display: block;
  }
  .row:hover .reaction-trigger,
  .row:focus-within .reaction-trigger,
  .reaction-trigger.open,
  .reaction-trigger:focus-visible {
    opacity: 1;
    transform: scale(1);
  }
  .reaction-trigger:hover {
    color: var(--widget-fg, oklch(20% 0.01 250));
  }

  .picker {
    position: absolute;
    top: 18px;
    right: -10px;
    display: flex;
    flex-direction: row;
    gap: 2px;
    padding: 4px 6px;
    background: var(--widget-bg, oklch(99% 0.003 250));
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    border-radius: 999px;
    box-shadow: 0 4px 14px color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 14%, transparent);
    animation: fade-in 140ms cubic-bezier(0.22, 1, 0.36, 1);
    z-index: 3;
  }
  .picker-btn {
    background: none;
    border: none;
    padding: 4px 6px;
    border-radius: 999px;
    font-family: inherit;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
    transition: background 120ms ease-out;
  }
  .picker-btn:hover {
    background: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 8%, transparent);
  }
  .picker-btn:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 1px;
  }
  .picker-btn.selected {
    background: color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 22%, var(--widget-bg, oklch(99% 0.003 250)));
  }

  .bubble {
    max-width: 100%;
    min-width: 48px;
    padding: 8px 12px;
    border-radius: 14px;
    background: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 6%, var(--widget-bg, oklch(99% 0.003 250)));
    color: var(--widget-fg, oklch(20% 0.01 250));
    font-size: 13.5px;
    line-height: 1.45;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  .row.outbound .bubble {
    background: var(--widget-accent, oklch(75% 0.165 75));
    color: var(--widget-fg, oklch(20% 0.012 250));
  }

  /* Asymmetric anchor only on the LAST bubble of a burst */
  .bubble.anchor-start {
    border-bottom-left-radius: 4px;
  }
  .bubble.anchor-end {
    border-bottom-right-radius: 4px;
  }

  .body {
    white-space: pre-wrap;
  }
  .body.muted {
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    font-style: italic;
  }

  .attachment {
    display: block;
    max-width: 100%;
    border-radius: 10px;
    background: var(--widget-border, oklch(92% 0.005 250));
  }

  .footer {
    display: flex;
    align-items: center;
    gap: 4px;
    margin: 4px 4px 0 4px;
    font-size: 10.5px;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }
  .row.outbound .footer {
    margin-right: 4px;
    margin-left: 0;
  }

  .time {
    font-weight: 500;
  }
  .sep {
    opacity: 0.6;
  }
  .status {
    font-size: 10px;
    font-weight: 500;
  }
  .status.pending {
    font-style: italic;
    font-weight: 600;
  }
  .status.read {
    color: var(--widget-accent-deep, oklch(50% 0.19 65));
    font-weight: 700;
  }
  .status.failed {
    color: oklch(55% 0.18 25);
    font-weight: 700;
  }
  .retry {
    background: none;
    border: none;
    color: oklch(55% 0.18 25);
    font-size: 10.5px;
    font-weight: 700;
    text-decoration: underline;
    cursor: pointer;
    padding: 0;
  }
  .retry:hover {
    text-decoration-thickness: 2px;
  }
  .retry:focus-visible {
    outline: 2px solid oklch(55% 0.18 25);
    outline-offset: 2px;
    border-radius: 3px;
  }

  @keyframes fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
</style>
